import styled from '@emotion/styled'
import { PlusCircle } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import Label from '../form/Label'
import TrainingSessionStep from './TrainingSessionStep'
import { TrainingSessionFormData } from '../../hooks/useTrainingSessionForm'
import { TrainingSessionStep as TrainingSessionStepType } from '../../types/training-session.type'
import { Exercise } from '../../types/exercise.type'

interface TrainingSessionStepsListProps {
    formData: TrainingSessionFormData
    exercises: Exercise[]
    onStepChange: <K extends keyof TrainingSessionStepType>(
        index: number,
        field: K,
        value: string | number | undefined
    ) => void
    onAddStep: () => void
    onRemoveStep: (index: number) => void
    onMoveStep: (index: number, direction: 'up' | 'down') => void
}

const TrainingSessionStepsList: React.FC<TrainingSessionStepsListProps> = ({
    formData,
    exercises,
    onStepChange,
    onAddStep,
    onRemoveStep,
    onMoveStep,
}) => {
    const { t } = useTranslation()

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <Label>{t('coach.trainingSessions.sessionSteps')}</Label>
                <AddStepButton
                    type="button"
                    onClick={onAddStep}
                    className="btn btn-link p-0"
                >
                    <PlusCircle size={20} className="me-1" />
                    {t('coach.trainingSessions.addStepButton')}
                </AddStepButton>
            </div>

            <div className="steps-container">
                {formData.steps.map((step, index) => (
                    <TrainingSessionStep
                        key={`step-${index}`}
                        step={step}
                        index={index}
                        totalSteps={formData.steps.length}
                        exercises={exercises}
                        onStepChange={onStepChange}
                        onMoveStep={onMoveStep}
                        onRemoveStep={onRemoveStep}
                    />
                ))}
            </div>
        </div>
    )
}

const AddStepButton = styled.button`
    font-size: 1.6rem;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    color: var(--primary-color);

    &:hover {
        color: var(--primary-dark);
    }
`

export default TrainingSessionStepsList
